import { pxToRem } from '@fluentsms/agentnet-web-components';
import { Box, createStyles, Grid, makeStyles, Theme, Tooltip, Typography } from '@material-ui/core';
import HelpOutline from '@material-ui/icons/HelpOutline';
import { KBLinks } from 'features/constants';
import { FincenReportContext, FincenReportContextInterface } from 'hooks/FincenReportContext';
import { FC, useContext, useState } from 'react';
import AgentNetButton from 'ui-kit/components/button/AgentNetButton';
import PageHeader from 'ui-kit/components/headers/PageHeader';
import { Payment, PaymentInformation } from '../types';
import ConsiderationForm from './components/ConsiderationForm';
import PaymentAccordion from './components/PaymentAccordion';
import { getInitialPaymentData } from './models/payment';

interface PaymentInformationContentProps {
  paymentInformation: PaymentInformation;
  onChange: (data: PaymentInformation) => void;
  isReadOnly?: boolean;
}

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    considerationSection: {
      padding: theme.spacing(0, 3, 0),
    },
    accordionContainer: {
      width: '100%',
      padding: theme.spacing(3, 3, 0),
    },

    infoIcon: {
      fontSize: pxToRem(18),
      marginLeft: theme.spacing(2),
      color: theme.palette.grey[600],
      verticalAlign: 'middle',
    },
  }),
);

const PaymentInformationContent: FC<PaymentInformationContentProps> = ({
  paymentInformation,
  onChange,
  isReadOnly = false,
}) => {
  const classes = useStyles();
  const fincenReportCtx = useContext<FincenReportContextInterface | null>(FincenReportContext);
  const { paymentValidationErrors } = fincenReportCtx ?? {};
  const [expandedPaymentId, setExpandedPaymentId] = useState<string | null>(
    paymentInformation.payments.length > 0 ? paymentInformation.payments[0].id : null,
  );

  const updatePaymentInformation = (updatedValues: Partial<PaymentInformation>) => {
    onChange({ ...paymentInformation, ...updatedValues });
  };

  const handleConsiderationEnabledChange = (enabled: boolean) => {
    updatePaymentInformation({ considerationEnabled: enabled });
  };

  const handleTotalConsiderationChange = (amount: number) => {
    updatePaymentInformation({ totalConsideration: amount });
  };

  const handleHasHardMoneyLoanChange = (hasLoan: boolean) => {
    updatePaymentInformation({
      hasHardMoneyLoan: hasLoan,
      hardMoneyLoanType: hasLoan ? paymentInformation.hardMoneyLoanType || '' : '',
      hardMoneyLoanOtherDescription: hasLoan ? paymentInformation.hardMoneyLoanOtherDescription || '' : '',
    });
  };

  const handleHardMoneyLoanTypeChange = (loanType: string) => {
    updatePaymentInformation({
      hardMoneyLoanType: loanType || '',
      hardMoneyLoanOtherDescription: loanType !== 'OTHER' ? '' : paymentInformation.hardMoneyLoanOtherDescription || '',
    });
  };

  const handleHardMoneyLoanOtherDescriptionChange = (description: string) => {
    updatePaymentInformation({ hardMoneyLoanOtherDescription: description });
  };

  const handleAddPayment = () => {
    updatePaymentInformation({
      payments: [...paymentInformation.payments, getInitialPaymentData(paymentInformation.payments.length)],
    });
  };

  const handleUpdatePayment = (index: number, updatedPayment: Payment) => {
    const updatedPayments = [...paymentInformation.payments];
    updatedPayments[index] = updatedPayment;
    updatePaymentInformation({ payments: updatedPayments });
  };

  const handleDeletePayment = (index: number) => {
    const updatedPayments = paymentInformation.payments.filter((_, i) => i !== index);
    updatePaymentInformation({ payments: updatedPayments });
  };

  const handleTogglePayment = (paymentId: string) => {
    setExpandedPaymentId(expandedPaymentId === paymentId ? null : paymentId);
  };

  return (
    <div
      style={{ pointerEvents: isReadOnly ? 'none' : 'auto', opacity: isReadOnly ? 0.6 : 1 }}
      tabIndex={isReadOnly ? -1 : undefined}
      onKeyDown={(e) => isReadOnly && e.preventDefault()}
    >
      <PageHeader
        title="Payment Information"
        subtitle={
          <>
            The report must include information about the total consideration paid for the reportable transfer as well
            as{' '}
            <strong>
              <em>each</em>
            </strong>{' '}
            payment made by or on behalf of each recorded transferee entity/trust.
          </>
        }
        menuItems={[
          {
            label: 'Knowledge Base',
            link: KBLinks.fincenReport,
          },
        ]}
      />

      <div className={classes.considerationSection}>
        <ConsiderationForm
          considerationEnabled={paymentInformation.considerationEnabled}
          totalConsideration={paymentInformation.totalConsideration}
          hasHardMoneyLoan={paymentInformation.hasHardMoneyLoan}
          hardMoneyLoanType={paymentInformation.hardMoneyLoanType}
          hardMoneyLoanOtherDescription={paymentInformation.hardMoneyLoanOtherDescription}
          onConsiderationEnabledChange={handleConsiderationEnabledChange}
          onTotalConsiderationChange={handleTotalConsiderationChange}
          onHasHardMoneyLoanChange={handleHasHardMoneyLoanChange}
          onHardMoneyLoanTypeChange={handleHardMoneyLoanTypeChange}
          onHardMoneyLoanOtherDescriptionChange={handleHardMoneyLoanOtherDescriptionChange}
          validationErrors={paymentValidationErrors}
        />
      </div>

      {paymentInformation.considerationEnabled && (
        <div className={classes.accordionContainer}>
          <Grid container spacing={4}>
            <Grid item sm={12}>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Typography variant="h3">
                  Payment Made By or On Behalf Of the Transferee Entity/Trust
                  <Tooltip title="Additional information about parties associated with this transferee">
                    <HelpOutline fontSize="small" className={classes.infoIcon} />
                  </Tooltip>
                </Typography>

                <AgentNetButton
                  variant="contained"
                  color="primary"
                  size="small"
                  onClick={handleAddPayment}
                  plusIcon
                  data-testid="fincen-payment-add-button"
                >
                  Add Payment
                </AgentNetButton>
              </Box>
            </Grid>
          </Grid>

          {paymentInformation.payments.map((payment, index) => (
            <Grid container spacing={8} key={payment.id}>
              <Grid item sm={12}>
                <PaymentAccordion
                  payment={payment}
                  index={index}
                  expanded={expandedPaymentId === payment.id}
                  onChange={(updatedPayment) => handleUpdatePayment(index, updatedPayment)}
                  onDelete={() => handleDeletePayment(index)}
                  onToggle={() => handleTogglePayment(payment.id)}
                  validationErrors={paymentValidationErrors}
                />
              </Grid>
            </Grid>
          ))}
        </div>
      )}
    </div>
  );
};

export default PaymentInformationContent;
